// /app/frontend/src/components/frugal-v2/components/AlertsDropdown.jsx
// Alerts dropdown component - bell icon for alerts only (NO emails)

import React, { forwardRef } from 'react';
import { X, AlertTriangle, AlertCircle, TrendingDown } from 'lucide-react';
import { getCategoryName, isRedAlert, isVitalAlert, getAlertMessage } from '../utils/alertHelpers';

const AlertsDropdown = forwardRef(({ alerts, onClose, t }, ref) => {
  const totalAlerts = alerts?.length || 0;

  return (
    <div
      ref={ref}
      className="fixed left-4 right-4 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-slate-800 rounded-xl shadow-xl border border-white/10 overflow-hidden z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="font-medium text-white">{t?.('frugal.alerts.title') || 'Alertas'}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="alerts-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {totalAlerts === 0 ? (
          <p className="px-4 py-6 text-center text-slate-500 text-sm">
            {t?.('frugal.alerts.noAlerts') || 'No hay alertas'}
          </p>
        ) : (
          alerts.map((alert, idx) => {
            // Cashflow alert
            if (alert.type === 'cashflow') {
              return (
                <div key={idx} className="px-4 py-3 border-b border-white/5 bg-rose-500/10" data-testid={`alert-cashflow-${idx}`}>
                  <div className="flex items-start gap-2">
                    <TrendingDown className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-rose-400">
                        {t?.('frugal.alerts.cashflowTitle') || 'Alerta de Flujo de Caja'}
                      </p>
                      <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                        {t?.('frugal.alerts.cashflowMessage') || 'Proyeccion de fondos insuficiente. Segun tus gastos programados y el saldo actual, se anticipa un faltante antes de tu proximo ingreso. Revisa tus gastos variables o ajusta tu presupuesto para mantener el equilibrio.'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            // Budget / Vital / Red alerts
            return (
              <div
                key={idx}
                className={`px-4 py-3 border-b border-white/5 ${
                  isRedAlert(alert.type) ? 'bg-red-500/10' : isVitalAlert(alert.type) ? 'bg-purple-500/10' : 'bg-amber-500/10'
                }`}
              >
                <div className="flex items-start gap-2">
                  {isRedAlert(alert.type) ? (
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  ) : isVitalAlert(alert.type) ? (
                    <AlertTriangle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      isRedAlert(alert.type) ? 'text-red-400' : isVitalAlert(alert.type) ? 'text-purple-400' : 'text-amber-400'
                    }`}>
                      {getCategoryName(alert.category, t)}
                    </p>
                    <p className="text-xs text-slate-300 mt-1">
                      {getAlertMessage(alert, t)}
                    </p>
                    {alert.remaining !== undefined && alert.remaining !== null && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {t?.('frugal.budget.remaining') || 'Restante'}: ${alert.remaining?.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

AlertsDropdown.displayName = 'AlertsDropdown';

export default AlertsDropdown;
